import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PackagesPersoService } from 'src/services/packages-perso.service';
import { ComponentCanDeactivate } from 'src/services/unsaved-changes.guard';
import { ContenuSessionService } from 'src/services/contenu.session.service';
import { PackageDetailModel, PackageInfoUpdateModel, QuestionModel } from 'src/models/pilotage/packageDetailModel';
import { DateFormatPipe } from 'src/pipes/date-format.pipe';

type LanguageCode = 'FR' | 'EN' | 'ES';

@Component({
    selector: 'app-edit-package-perso',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        MatProgressSpinnerModule,
        MatIconModule,
        MatExpansionModule,
        DateFormatPipe
    ],
    templateUrl: './edit-package-perso.component.html',
    styleUrls: ['./edit-package-perso.component.scss']
})
export class EditPackagePersoComponent implements OnInit, ComponentCanDeactivate {
  packageId: number = 0;
  packageDetail: PackageDetailModel | null = null;
  isLoading = false;
  isSaving = false;
  isFormValidState = false;
  hasExistingSession = false;

  private pristineState = '';

  packageInfo: PackageInfoUpdateModel = {
    nomFR: '',
    descriptionFR: '',
    nomEN: '',
    descriptionEN: '',
    nomES: '',
    descriptionES: '',
    questions: [],
    canSeekForward: false,
    readingConfirmationRequired: false
  };

  availableLanguages = {
    FR: false,
    EN: false,
    ES: false
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private packagesPersoService: PackagesPersoService,
    private contenuSessionService: ContenuSessionService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.capturePristineState();
    this.route.params.subscribe(params => {
      this.packageId = +params['id'];
      if (this.packageId) {
        this.checkSessionExistence();
        this.loadPackageDetails();
      }
    });
  }

  private checkSessionExistence(): void {
    this.contenuSessionService.hasSessionWithPackage(this.packageId).subscribe({
      next: (hasSession: boolean) => {
        this.hasExistingSession = hasSession;
      },
      error: (error) => {
        console.error('Erreur lors de la vérification des sessions:', error);
      }
    });
  }

  loadPackageDetails(): void {
    this.isLoading = true;
    this.packagesPersoService.getPackageDetails(this.packageId)
      .subscribe({
        next: (packageDetail: PackageDetailModel) => {
          this.packageDetail = packageDetail;
          this.availableLanguages = {
            FR: packageDetail.hasFR,
            EN: packageDetail.hasEN,
            ES: packageDetail.hasES
          };

          this.packageInfo = {
            nomFR: packageDetail.nomFR || '',
            descriptionFR: packageDetail.descriptionFR || '',
            nomEN: packageDetail.nomEN || '',
            descriptionEN: packageDetail.descriptionEN || '',
            nomES: packageDetail.nomES || '',
            descriptionES: packageDetail.descriptionES || '',
            questions: packageDetail.questions ? [...packageDetail.questions] : [],
            canSeekForward: !!packageDetail.canSeekForward,
            readingConfirmationRequired: !!packageDetail.readingConfirmationRequired
          };

          this.updateFormValidState();
          this.capturePristineState();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Erreur lors du chargement des détails du package:', error);
          this.snackBar.open(
            this.translate.instant('PACKAGES_PERSO_MANAGEMENT.LOAD_ERROR'),
            'Fermer',
            { duration: 5000 }
          );
          this.isLoading = false;
          this.goBack();
        }
      });
  }

  saveChanges(): void {
    if (this.isSaving) {
      return;
    }

    if (this.hasExistingSession) {
      this.snackBar.open(
        this.translate.instant('PACKAGES_PERSO_MANAGEMENT.EDIT_DISABLED_SESSION_EXISTS'),
        'Fermer',
        { duration: 5000 }
      );
      return;
    }

    if (!this.isFormValidState) {
      this.snackBar.open(
        this.translate.instant('PACKAGES_PERSO_MANAGEMENT.VALIDATION_FORM_INVALID'),
        'Fermer',
        { duration: 5000 }
      );
      return;
    }

    this.isSaving = true;
    this.packagesPersoService.updatePackageInfo(this.packageId, this.packageInfo)
      .subscribe({
        next: () => {
          this.snackBar.open(
            this.translate.instant('PACKAGES_PERSO_MANAGEMENT.UPDATE_SUCCESS'),
            'Fermer',
            { duration: 3000 }
          );
          this.isSaving = false;
          this.capturePristineState();
          this.goBack();
        },
        error: (error) => {
          console.error('Erreur lors de la mise à jour:', error);
          this.snackBar.open(
            this.translate.instant('PACKAGES_PERSO_MANAGEMENT.UPDATE_ERROR'),
            'Fermer',
            { duration: 5000 }
          );
          this.isSaving = false;
        }
      });
  }

  updateFormValidState(): void {
    const previousState = this.isFormValidState;
    this.isFormValidState = this.isFormValid();
    if (previousState !== this.isFormValidState) {
      this.cdr.detectChanges();
    }
  }

  isFormValid(): boolean {
    if (this.availableLanguages.FR && (!this.packageInfo.nomFR || this.packageInfo.nomFR.trim() === '')) {
      return false;
    }
    if (this.availableLanguages.EN && (!this.packageInfo.nomEN || this.packageInfo.nomEN.trim() === '')) {
      return false;
    }
    if (this.availableLanguages.ES && (!this.packageInfo.nomES || this.packageInfo.nomES.trim() === '')) {
      return false;
    }

    if (this.packageInfo.questions && this.packageInfo.questions.length > 0) {
      for (const question of this.packageInfo.questions) {
        if (this.availableLanguages.FR && (!question.textFR || question.textFR.trim() === '')) {
          return false;
        }
        if (this.availableLanguages.EN && (!question.textEN || question.textEN.trim() === '')) {
          return false;
        }
        if (this.availableLanguages.ES && (!question.textES || question.textES.trim() === '')) {
          return false;
        }

        if (question.answers && question.answers.length > 0) {
          for (const answer of question.answers) {
            if (this.availableLanguages.FR && (!answer.textFR || answer.textFR.trim() === '')) {
              return false;
            }
            if (this.availableLanguages.EN && (!answer.textEN || answer.textEN.trim() === '')) {
              return false;
            }
            if (this.availableLanguages.ES && (!answer.textES || answer.textES.trim() === '')) {
              return false;
            }
          }
        }
      }
    }

    return true;
  }

  getSelectedLanguagesArray(): LanguageCode[] {
    return Object.entries(this.availableLanguages)
      .filter(([_, value]) => value)
      .map(([key]) => key as LanguageCode);
  }

  goBack(): void {
    this.location.back();
  }

  goBackToList(): void {
    this.router.navigate(['/scorm-perso-managment']);
  }

  hasUnsavedChanges(): boolean {
    if (this.isSaving) {
      return false;
    }

    return JSON.stringify(this.packageInfo) !== this.pristineState;
  }

  private capturePristineState(): void {
    this.pristineState = JSON.stringify(this.packageInfo);
  }

  cancelEdit(): void {
    if (this.packageDetail) {
      this.packageInfo = {
        nomFR: this.packageDetail.nomFR || '',
        descriptionFR: this.packageDetail.descriptionFR || '',
        nomEN: this.packageDetail.nomEN || '',
        descriptionEN: this.packageDetail.descriptionEN || '',
        nomES: this.packageDetail.nomES || '',
        descriptionES: this.packageDetail.descriptionES || '',
        questions: this.packageDetail.questions ? [...this.packageDetail.questions] : [],
        canSeekForward: !!this.packageDetail.canSeekForward,
        readingConfirmationRequired: !!this.packageDetail.readingConfirmationRequired
      };
    }
    this.goBack();
  }

  hasQuestions(): boolean {
    return !!(this.packageInfo.questions && this.packageInfo.questions.length > 0);
  }

  updateQuestionText(questionIndex: number, language: LanguageCode, value: string): void {
    if (this.packageInfo.questions && this.packageInfo.questions[questionIndex]) {
      const question = this.packageInfo.questions[questionIndex];
      switch (language) {
        case 'FR':
          question.textFR = value;
          break;
        case 'EN':
          question.textEN = value;
          break;
        case 'ES':
          question.textES = value;
          break;
      }
      this.updateFormValidState();
    }
  }

  updateAnswerText(questionIndex: number, answerIndex: number, language: LanguageCode, value: string): void {
    if (this.packageInfo.questions &&
      this.packageInfo.questions[questionIndex] &&
      this.packageInfo.questions[questionIndex].answers &&
      this.packageInfo.questions[questionIndex].answers[answerIndex]) {
      const answer = this.packageInfo.questions[questionIndex].answers[answerIndex];
      switch (language) {
        case 'FR':
          answer.textFR = value;
          break;
        case 'EN':
          answer.textEN = value;
          break;
        case 'ES':
          answer.textES = value;
          break;
      }
      this.updateFormValidState();
    }
  }

  isFieldEmpty(value: string | undefined | null): boolean {
    return !value || value.trim() === '';
  }

  isNameFieldValid(language: LanguageCode): boolean {
    if (!this.availableLanguages[language]) return true;

    switch (language) {
      case 'FR': return !this.isFieldEmpty(this.packageInfo.nomFR);
      case 'EN': return !this.isFieldEmpty(this.packageInfo.nomEN);
      case 'ES': return !this.isFieldEmpty(this.packageInfo.nomES);
      default: return true;
    }
  }

  isQuestionTextValid(question: QuestionModel, language: LanguageCode): boolean {
    if (!this.availableLanguages[language]) return true;

    switch (language) {
      case 'FR': return !this.isFieldEmpty(question.textFR);
      case 'EN': return !this.isFieldEmpty(question.textEN);
      case 'ES': return !this.isFieldEmpty(question.textES);
      default: return true;
    }
  }

  isAnswerTextValid(answer: any, language: LanguageCode): boolean {
    if (!this.availableLanguages[language]) return true;

    switch (language) {
      case 'FR': return !this.isFieldEmpty(answer.textFR);
      case 'EN': return !this.isFieldEmpty(answer.textEN);
      case 'ES': return !this.isFieldEmpty(answer.textES);
      default: return true;
    }
  }

  updateNameField(language: LanguageCode, value: string): void {
    switch (language) {
      case 'FR':
        this.packageInfo.nomFR = value;
        break;
      case 'EN':
        this.packageInfo.nomEN = value;
        break;
      case 'ES':
        this.packageInfo.nomES = value;
        break;
    }
    this.updateFormValidState();
  }

  updateDescriptionField(language: LanguageCode, value: string): void {
    switch (language) {
      case 'FR':
        this.packageInfo.descriptionFR = value;
        break;
      case 'EN':
        this.packageInfo.descriptionEN = value;
        break;
      case 'ES':
        this.packageInfo.descriptionES = value;
        break;
    }
    this.updateFormValidState();
  }

  get isButtonDisabled(): boolean {
    return !this.isFormValidState || this.isSaving || this.hasExistingSession;
  }
}